import { type FetchPageFn } from "@/block-system/blocks/Table/Block/TableData";
import { TABLE_RECORDS_PAGINATION_PAGE_SIZES } from "@/block-system/blocks/Table/constants";
import { Button } from "@/block-system/brickz/components/ui/Button";
import { DropdownSingleSelect } from "@/block-system/brickz/components/ui/DropdownSelect";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/block-system/brickz/components/ui/tooltip";
import { cn } from "@/block-system/brickz/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Fragment, useId } from "react";

export function TablePagination({
  pageSize,
  setPageSize,
  fetchPage,
  canFetchPreviousPage,
  canFetchNextPage,
  canSetPageSize,
  supportsPageJumping,
}: {
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  fetchPage: FetchPageFn;
  canFetchPreviousPage: boolean;
  canFetchNextPage: boolean;
  canSetPageSize: boolean;
  supportsPageJumping: boolean;
}) {
  return (
    <Fragment>
      <RowsPerPageSelect
        pageSize={pageSize}
        setPageSize={setPageSize}
        canSetPageSize={canSetPageSize}
      />
      <PaginationControls
        supportsPageJumping={supportsPageJumping}
        fetchPage={fetchPage}
        canFetchPreviousPage={canFetchPreviousPage}
        canFetchNextPage={canFetchNextPage}
      />
    </Fragment>
  );
}

function RowsPerPageSelect({
  pageSize,
  setPageSize,
  canSetPageSize,
}: {
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  canSetPageSize: boolean;
}) {
  const rowsPerPageSelectId = useId();

  return (
    <div className="flex flex-row items-center gap-2 justify-self-center">
      <label
        className="whitespace-nowrap text-sm font-normal leading-tight text-foreground dark:text-foreground-subtle"
        htmlFor={rowsPerPageSelectId}
      >
        Rows per page
      </label>
      <DropdownSingleSelect
        searchable={false}
        menuAriaLabel={"Select the number of rows per page"}
        className={cn(
          /**
           * To match the pagination button styles.
           */
          "bg-card hover:bg-secondary",
          /**
           * We have to use `[&&]` to override some default styles to match the designs.
           */
          "text-sm leading-tight text-foreground-subtle",
          "[&&]:h-[1.875rem] [&&]:min-h-[1.875rem] [&&]:min-w-14 [&&]:px-2 [&&]:py-1"
        )}
        id={rowsPerPageSelectId}
        value={pageSize.toString()}
        isDisabled={!canSetPageSize}
        isRequired={true}
        onChange={(newValue) => {
          if (!newValue) {
            return;
          }

          setPageSize(Number(newValue));
        }}
        options={TABLE_RECORDS_PAGINATION_PAGE_SIZES.map((pageSize) => {
          return {
            label: pageSize.toString(),
            value: pageSize.toString(),
          };
        })}
      />
    </div>
  );
}

function PaginationControls({
  fetchPage,
  canFetchPreviousPage,
  canFetchNextPage,
  supportsPageJumping,
}: {
  fetchPage: FetchPageFn;
  canFetchPreviousPage: boolean;
  canFetchNextPage: boolean;
  supportsPageJumping: boolean;
}) {
  return (
    <TooltipProvider>
      <div className="relative flex flex-row items-center gap-[0.125rem]">
        {supportsPageJumping ? (
          <FirstPageButton
            canFetchPreviousPage={canFetchPreviousPage}
            fetchPage={fetchPage}
          />
        ) : null}

        <PreviousPageButton
          canFetchPreviousPage={canFetchPreviousPage}
          fetchPage={fetchPage}
        />

        <NextPageButton
          canFetchNextPage={canFetchNextPage}
          fetchPage={fetchPage}
        />

        {supportsPageJumping ? (
          <LastPageButton
            canFetchNextPage={canFetchNextPage}
            fetchPage={fetchPage}
          />
        ) : null}
      </div>
    </TooltipProvider>
  );
}
function FirstPageButton({
  canFetchPreviousPage,
  fetchPage,
}: {
  canFetchPreviousPage: boolean;
  fetchPage: FetchPageFn;
}) {
  return (
    <Tooltip delayDuration={undefined}>
      <TooltipTrigger asChild={true}>
        <Button
          disabled={!canFetchPreviousPage}
          onClick={() => fetchPage({ direction: "first" })}
          variant={"outline"}
          className={cn(
            "flex h-[1.875rem] w-[1.875rem] items-center justify-center",
            /**
             * To make sure the Tooltip triggers even when the button is disabled.
             */
            "disabled:pointer-events-auto"
          )}
          size={"icon"}
        >
          <span className="sr-only">First page</span>
          <ChevronsLeft />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <span>First page</span>
      </TooltipContent>
    </Tooltip>
  );
}

function PreviousPageButton({
  canFetchPreviousPage,
  fetchPage,
}: {
  canFetchPreviousPage: boolean;
  fetchPage: FetchPageFn;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild={true}>
        <Button
          disabled={!canFetchPreviousPage}
          onClick={() =>
            fetchPage({
              direction: "previous",
            })
          }
          variant={"outline"}
          className={cn(
            "flex h-[1.875rem] w-[1.875rem] items-center justify-center",
            /**
             * To make sure the Tooltip triggers even when the button is disabled.
             */
            "disabled:pointer-events-auto"
          )}
          size={"icon"}
        >
          <span className="sr-only">Previous page</span>
          <ChevronLeft />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <span>Previous page</span>
      </TooltipContent>
    </Tooltip>
  );
}

function NextPageButton({
  canFetchNextPage,
  fetchPage,
}: {
  canFetchNextPage: boolean;
  fetchPage: FetchPageFn;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild={true}>
        <Button
          disabled={!canFetchNextPage}
          onClick={() =>
            fetchPage({
              direction: "next",
            })
          }
          variant={"outline"}
          className={cn(
            "flex h-[1.875rem] w-[1.875rem] items-center justify-center",
            /**
             * To make sure the Tooltip triggers even when the button is disabled.
             */
            "disabled:pointer-events-auto"
          )}
          size={"icon"}
        >
          <span className="sr-only">Next page</span>
          <ChevronRight />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <span>Next page</span>
      </TooltipContent>
    </Tooltip>
  );
}

function LastPageButton({
  canFetchNextPage,
  fetchPage,
}: {
  canFetchNextPage: boolean;
  fetchPage: FetchPageFn;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild={true}>
        <Button
          disabled={!canFetchNextPage}
          onClick={() =>
            fetchPage({
              direction: "last",
            })
          }
          variant={"outline"}
          className={cn(
            "flex h-[1.875rem] w-[1.875rem] items-center justify-center",
            /**
             * To make sure the Tooltip triggers even when the button is disabled.
             */
            "disabled:pointer-events-auto"
          )}
          size={"icon"}
        >
          <span className="sr-only">Last page</span>
          <ChevronsRight />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <span>Last page</span>
      </TooltipContent>
    </Tooltip>
  );
}
